import React, { useMemo } from 'react';
import { Calendar, Clock, Flag, Trophy } from 'lucide-react';
import dayjs from 'dayjs';
import { formatTime } from '../utils/formatTime';

/**
 * EventsHero - Quick stats and next event spotlight for Events page
 *
 * Shows:
 * - Next upcoming event card (featured)
 * - Quick stats: events this season, total signups, etc.
 */
const EventsHero = ({
  upcomingEvents = [],
  pastEvents = [],
  onEventClick,
}) => {
  // Calculate stats
  const stats = useMemo(() => {
    const completedEvents = pastEvents.length;
    const upcomingCount = upcomingEvents.length;

    // Calculate total signups across upcoming events if available
    const totalSignups = upcomingEvents.reduce((sum, event) => {
      return sum + (event.signup_count || 0);
    }, 0);

    return {
      completedEvents,
      upcomingCount,
      totalSignups,
    };
  }, [upcomingEvents, pastEvents]);

  // Get next upcoming event
  const nextEvent = upcomingEvents[0];

  // Get event date info
  const getEventDateInfo = (event) => {
    if (!event) return null;
    const eventDate = dayjs(event.date);
    const isToday = eventDate.isSame(dayjs(), 'day');
    const isTomorrow = eventDate.isSame(dayjs().add(1, 'day'), 'day');
    const daysUntil = eventDate.diff(dayjs(), 'day');

    return {
      isToday,
      isTomorrow,
      daysUntil,
      formattedDate: eventDate.format('dddd, MMMM D'),
      shortDate: eventDate.format('MMM D'),
    };
  };

  const nextEventInfo = getEventDateInfo(nextEvent);

  // Check if next event has a course image
  const courseImage = nextEvent?.course_image;

  return (
    <div className="mb-6">
      {/* Quick Stats Row */}
      <div className="flex flex-wrap items-center gap-6 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
            {stats.completedEvents}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">Events Completed</span>
        </div>
        <div className="w-px h-6 bg-gray-300 dark:bg-navy-600" />
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-forest-600 dark:text-forest-400 tabular-nums">
            {stats.upcomingCount}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">Upcoming</span>
        </div>
        {stats.totalSignups > 0 && (
          <>
            <div className="w-px h-6 bg-gray-300 dark:bg-navy-600 hidden sm:block" />
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-800 dark:text-gray-200 tabular-nums">
                {stats.totalSignups}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">Total Signups</span>
            </div>
          </>
        )}
      </div>

      {/* Next Event Spotlight */}
      {nextEvent && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => onEventClick && onEventClick(nextEvent)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onEventClick && onEventClick(nextEvent);
            }
          }}
          aria-label={`View details for ${nextEvent.course_name}`}
          className={`
            relative overflow-hidden rounded-xl p-5
            bg-gradient-to-r from-forest-600 to-forest-700
            dark:from-forest-800 dark:to-navy-800
            text-white shadow-lg cursor-pointer
            hover:shadow-xl transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-forest-400 focus:ring-offset-2
            group
          `}
        >
          {/* Background - Course Image or Pattern */}
          {courseImage ? (
            <>
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                style={{ backgroundImage: `url(${courseImage})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-forest-900/80 via-forest-800/75 to-forest-700/70 dark:from-navy-900/85 dark:via-navy-800/80 dark:to-forest-900/75" />
            </>
          ) : (
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                    <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#grid)" />
              </svg>
            </div>
          )}

          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Left - Event Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Flag className="w-5 h-5 text-gold-400" />
                <span className="text-sm font-semibold text-gold-400 uppercase tracking-wider">
                  Next Event
                </span>
                {nextEventInfo?.isToday && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-white text-forest-700 animate-pulse">
                    TODAY
                  </span>
                )}
                {nextEventInfo?.isTomorrow && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gold-400 text-forest-900">
                    TOMORROW
                  </span>
                )}
              </div>

              <h3 className="text-2xl md:text-3xl font-bold mb-2 group-hover:text-gold-300 transition-colors">
                {nextEvent.course_name}
              </h3>

              <div className="flex flex-wrap items-center gap-4 text-forest-100">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-forest-300" />
                  <span className="font-medium">{nextEventInfo?.formattedDate}</span>
                </div>
                {nextEvent.tee_time && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-forest-300" />
                    <span className="font-semibold text-gold-300">
                      {formatTime(nextEvent.tee_time)}
                    </span>
                  </div>
                )}
              </div>

              {/* Badges */}
              <div className="flex items-center gap-2 mt-3">
                {nextEvent.is_major && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gold-400 text-forest-900">
                    <Trophy className="w-3 h-3" />
                    Major
                  </span>
                )}
                {nextEvent.cost && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-forest-500 text-white">
                    ${Number(nextEvent.cost).toFixed(0)}
                  </span>
                )}
              </div>
            </div>

            {/* Right - Days Until */}
            {nextEventInfo && !nextEventInfo.isToday && !nextEventInfo.isTomorrow && nextEventInfo.daysUntil > 0 && (
              <div className="flex-shrink-0 text-center md:text-right">
                <div className="inline-flex flex-col items-center justify-center w-20 h-20 rounded-full bg-white/10 backdrop-blur">
                  <span className="text-3xl font-bold text-white tabular-nums">
                    {nextEventInfo.daysUntil}
                  </span>
                  <span className="text-xs text-forest-200 uppercase tracking-wider">
                    {nextEventInfo.daysUntil === 1 ? 'Day' : 'Days'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Click hint */}
          <div className="absolute bottom-2 right-3 text-xs text-forest-300 opacity-0 group-hover:opacity-100 transition-opacity">
            Click for details
          </div>
        </div>
      )}

      {/* No upcoming events message */}
      {!nextEvent && (
        <div className="rounded-xl p-6 bg-gray-100 dark:bg-navy-800 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">
            No upcoming events scheduled
          </p>
        </div>
      )}
    </div>
  );
};

export default EventsHero;
